/**
 * Hook for subscribing to backup task progress events.
 * Automatically updates the backup/restore store when progress events arrive.
 */

import { useEffect } from 'react';
import { subscribeToTaskProgress } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';

/**
 * Hook that subscribes to backup task progress events and updates the store.
 * Should be mounted once at the app level (e.g., in App.tsx or a provider).
 */
export function useTaskProgressSubscription(): void {
  const { updateOperationProgress } = useBackupRestoreStore();

  useEffect(() => {
    const unsubscribe = subscribeToTaskProgress((progress) => {
      console.log('[useTaskProgress] Received progress:', progress);
      updateOperationProgress(progress.taskId, progress);
    });

    return unsubscribe;
  }, [updateOperationProgress]);
}

/**
 * Hook to get the current status of a specific operation.
 */
export function useOperationStatus(operationId: string | null) {
  const operation = useBackupRestoreStore((state) =>
    operationId ? state.operations.find((op) => op.id === operationId) : undefined
  );

  return operation;
}

/**
 * Hook to get all active (non-completed) operations.
 */
export function useActiveOperations() {
  const operations = useBackupRestoreStore((state) =>
    state.operations.filter(
      (op) => op.status !== 'succeeded' && op.status !== 'failed' && op.status !== 'cancelled'
    )
  );

  return operations;
}
